// NPC Chirp accounts (~60) and deterministic handles for anonymous NPC traders.

import { TUNABLES } from "@degen/shared/config/tunables";
import { HANDLE_FRAGMENTS, type Personality } from "@degen/shared/config/content";
import { hashString, type Rng } from "../rng";

export interface NpcAccount {
  id: string; // "acct:N" — matches the ids copytraders follow and launcher shills use
  name: string;
  handle: string;
  pfpSeed: number;
  followers: number;
  personality: Personality;
  rugShills: number; // times this account shilled a coin that later rugged (callout evidence)
}

const PERSONALITIES: Record<Personality, number> = {
  alpha: 0.2, doomer: 0.12, shillbot: 0.18, sleuth: 0.08, influencer: 0.16, builder: 0.1, memelord: 0.16,
};

function makeHandle(rng: Rng): string {
  const a = rng.pick(HANDLE_FRAGMENTS);
  const b = rng.pick(HANDLE_FRAGMENTS);
  const sep = rng.pick(["", "_", ""]);
  const num = rng.chance(0.3) ? String(rng.int(1, 999)) : "";
  return `${a}${sep}${b}${num}`.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 15);
}

function displayFromHandle(h: string): string {
  return h
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}

export function createAccounts(rng: Rng): NpcAccount[] {
  const used = new Set<string>();
  const out: NpcAccount[] = [];
  const [lo, hi] = TUNABLES.social.npcFollowers;
  for (let i = 0; i < TUNABLES.npc.accounts; i++) {
    let handle = makeHandle(rng);
    while (used.has(handle) || handle.length < 3) handle = makeHandle(rng);
    used.add(handle);
    const personality = rng.weighted(PERSONALITIES) as Personality;
    // Log-uniform follower counts: lots of small accounts, a few huge ones.
    const followers = Math.round(Math.exp(rng.range(Math.log(lo), Math.log(hi))));
    out.push({ id: `acct:${i}`, name: displayFromHandle(handle), handle, pfpSeed: rng.seed(), followers, personality, rugShills: 0 });
  }
  return out;
}

/** Stable display handle for an anonymous NPC trader id (e.g. "n42") in trade feeds. */
export function traderHandle(id: string): string {
  if (id === "player") return "you";
  if (id.startsWith("dev:")) return "dev";
  const h = hashString(id);
  const a = HANDLE_FRAGMENTS[h % HANDLE_FRAGMENTS.length];
  const b = HANDLE_FRAGMENTS[(h >>> 8) % HANDLE_FRAGMENTS.length];
  return `${a}${b}`.replace(/[^a-z0-9_]/gi, "").slice(0, 14).toLowerCase();
}
